import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Wand2,
  Plus,
  Trash2,
  GripVertical,
  Clock,
  FileText,
  CheckCircle,
  MessageSquare,
  Vote,
  Info,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Save,
  X,
  ListOrdered,
  AlertCircle,
  BookTemplate,
  FolderOpen,
  Copy,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Outcome type configuration
const OUTCOME_TYPES = {
  Discuss: { color: "bg-blue-100 text-blue-700", icon: MessageSquare },
  Decide: { color: "bg-amber-100 text-amber-700", icon: Vote },
  Approve: { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  Note: { color: "bg-slate-100 text-slate-700", icon: Info },
  Inform: { color: "bg-purple-100 text-purple-700", icon: FileText },
};

// Recurring items configuration
const RECURRING_ITEMS = [
  { key: "call_to_order", label: "Call to Order / Opening", group: "opening" },
  { key: "quorum_confirmation", label: "Quorum Confirmation", group: "opening" },
  { key: "approval_previous_minutes", label: "Approval of Previous Minutes", group: "opening" },
  { key: "matters_arising", label: "Matters Arising", group: "opening" },
  { key: "chair_remarks", label: "Chair's Remarks", group: "opening" },
  { key: "finance_update", label: "Finance Update", group: "updates" },
  { key: "operations_update", label: "Operations Update", group: "updates" },
  { key: "risk_compliance_update", label: "Risk/Compliance Update", group: "updates" },
  { key: "resolutions_approvals", label: "Resolutions/Approvals", group: "business" },
  { key: "aob", label: "Any Other Business (AOB)", group: "closing" },
  { key: "next_meeting", label: "Next Meeting", group: "closing" },
  { key: "adjournment", label: "Adjournment", group: "closing" },
];

export default function AgendaBuilder({ meetingId, meeting, onUpdate }) {
  const [agendaItems, setAgendaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewItems, setPreviewItems] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [agendaGenerated, setAgendaGenerated] = useState(false);
  const [totalDuration, setTotalDuration] = useState(0);
  
  // Template state
  const [templates, setTemplates] = useState([]);
  const [libraryTemplates, setLibraryTemplates] = useState([]);
  const [templateCategories, setTemplateCategories] = useState({});
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [previewTemplate, setPreviewTemplate] = useState(null);
  
  // Template customization state
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [customizeTemplate, setCustomizeTemplate] = useState(null);
  const [customizedItems, setCustomizedItems] = useState([]);
  
  // Wizard state
  const [wizardData, setWizardData] = useState({
    meeting_type: "Board",
    committee_name: meeting?.committee_name || "",
    objectives: "",
    topics: "",
    recurring_toggles: {
      call_to_order: true,
      quorum_confirmation: true,
      approval_previous_minutes: true,
      matters_arising: true,
      aob: true,
      next_meeting: true,
      adjournment: true,
    },
    include_timings: true,
    include_docs_checklist: false,
  });
  
  // Edit item state
  const [editingItem, setEditingItem] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({
    title: "",
    outcome_type: "Discuss",
    duration_mins: 15,
    docs_required: [],
    description: "",
  });

  const fetchAgenda = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/meetings/${meetingId}/agenda`);
      setAgendaItems(response.data.agenda_items || []);
      setAgendaGenerated(response.data.agenda_generated || false);
      setHasChanges(false);
    } catch (error) {
      console.error("Failed to fetch agenda");
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/agenda-templates`);
      setTemplates(response.data || []);
    } catch (error) {
      console.error("Failed to fetch templates");
    }
  };

  const fetchLibrary = async () => {
    try {
      const response = await axios.get(`${API}/agenda-templates/library`);
      setLibraryTemplates(response.data.templates || []);
      setTemplateCategories(response.data.categories || {});
    } catch (error) {
      console.error("Failed to fetch template library");
    }
  };

  useEffect(() => {
    fetchAgenda();
    fetchTemplates();
    fetchLibrary();
  }, [fetchAgenda]);

  useEffect(() => {
    const duration = agendaItems.reduce((sum, item) => sum + (item.duration_mins || 0), 0);
    setTotalDuration(duration);
  }, [agendaItems]);

  const handleApplyTemplate = async (templateId) => {
    try {
      await axios.post(`${API}/agenda-templates/${templateId}/apply/${meetingId}`);
      toast.success("Template applied successfully");
      fetchAgenda();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply template");
    }
  };

  const handleApplyLibraryTemplate = async (templateId) => {
    try {
      await axios.post(`${API}/agenda-templates/library/${templateId}/apply/${meetingId}`);
      toast.success("Template applied successfully");
      setShowTemplateLibrary(false);
      fetchAgenda();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply template");
    }
  };

  // Handler for customizing a template before applying
  const handleCustomizeTemplate = (template) => {
    setCustomizeTemplate(template);
    // Deep copy the items for editing
    setCustomizedItems(template.items.map((item, idx) => ({
      ...item,
      id: item.id || `temp-${idx}`,
      order: item.order || idx + 1,
    })));
    setShowCustomizeModal(true);
    setShowTemplateLibrary(false);
  };

  const handleUpdateCustomizedItem = (index, field, value) => {
    const updated = [...customizedItems];
    updated[index] = { ...updated[index], [field]: value };
    setCustomizedItems(updated);
  };

  const handleRemoveCustomizedItem = (index) => {
    setCustomizedItems(customizedItems.filter((_, i) => i !== index));
  };

  const handleAddCustomizedItem = () => {
    setCustomizedItems([
      ...customizedItems,
      {
        id: `new-${Date.now()}`,
        title: "",
        description: "",
        duration_mins: 10,
        outcome_type: "discussion",
        order: customizedItems.length + 1,
      }
    ]);
  };

  const handleApplyCustomizedTemplate = async () => {
    if (customizedItems.length === 0) {
      toast.error("Add at least one agenda item");
      return;
    }
    
    try {
      // Apply the customized items to the meeting
      await axios.put(`${API}/meetings/${meetingId}/agenda`, {
        agenda_items: customizedItems.map((item, idx) => ({
          title: item.title,
          description: item.description || "",
          duration_mins: item.duration_mins || 10,
          outcome_type: item.outcome_type || "discussion",
          order: idx + 1,
        }))
      });
      
      toast.success(`Applied customized template with ${customizedItems.length} items`);
      setShowCustomizeModal(false);
      setCustomizeTemplate(null);
      setCustomizedItems([]);
      fetchAgenda();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply customized template");
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Please enter a template name");
      return;
    }
    
    setSavingTemplate(true);
    try {
      await axios.post(`${API}/agenda-templates/from-meeting/${meetingId}?name=${encodeURIComponent(templateName)}&description=${encodeURIComponent(templateDescription)}`);
      toast.success("Template saved successfully");
      setShowSaveTemplate(false);
      setTemplateName("");
      setTemplateDescription("");
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleGenerateAgenda = async () => {
    setGenerating(true);
    try {
      const topics = wizardData.topics
        .split("\n")
        .map(t => t.trim())
        .filter(t => t.length > 0);

      const response = await axios.post(`${API}/meetings/${meetingId}/agenda/generate`, {
        meeting_type: wizardData.meeting_type,
        committee_name: wizardData.committee_name,
        objectives: wizardData.objectives,
        topics,
        recurring_toggles: wizardData.recurring_toggles,
        include_timings: wizardData.include_timings,
        include_docs_checklist: wizardData.include_docs_checklist,
      });

      setPreviewItems(response.data.agenda_items);
      setShowWizard(false);
      setShowPreview(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate agenda");
    } finally {
      setGenerating(false);
    }
  };

  const handleApplyPreview = async () => {
    try {
      await axios.post(`${API}/meetings/${meetingId}/agenda/apply`, {
        agenda_items: previewItems,
      });
      setAgendaItems(previewItems);
      setAgendaGenerated(true);
      setShowPreview(false);
      setHasChanges(false);
      toast.success("Agenda applied to meeting");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Failed to apply agenda");
    }
  };

  const handleSaveAgenda = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/meetings/${meetingId}/agenda`, {
        agenda_items: agendaItems,
      });
      setHasChanges(false);
      toast.success("Agenda saved");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Failed to save agenda");
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    const item = {
      id: `agenda_${Date.now()}`,
      order: agendaItems.length + 1,
      ...newItem,
      docs_required: newItem.docs_required || [],
    };

    setAgendaItems([...agendaItems, item]);
    setShowAddItem(false);
    setNewItem({
      title: "",
      outcome_type: "Discuss",
      duration_mins: 15,
      docs_required: [],
      description: "",
    });
    setHasChanges(true);
  };

  const handleDeleteItem = (itemId) => {
    setAgendaItems(agendaItems.filter(item => item.id !== itemId));
    setHasChanges(true);
  };

  const handleUpdateItem = (itemId, field, value) => {
    setAgendaItems(agendaItems.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
    setHasChanges(true);
  };

  const handleMoveItem = (index, direction) => {
    const newItems = [...agendaItems];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newItems.length) return;
    
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    
    // Update orders
    newItems.forEach((item, idx) => {
      item.order = idx + 1;
    });
    
    setAgendaItems(newItems);
    setHasChanges(true);
  };

  const toggleRecurring = (key) => {
    setWizardData({
      ...wizardData,
      recurring_toggles: {
        ...wizardData.recurring_toggles,
        [key]: !wizardData.recurring_toggles[key],
      },
    });
  };

  const getOutcomeConfig = (type) => OUTCOME_TYPES[type] || OUTCOME_TYPES.Discuss;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ListOrdered className="w-5 h-5 text-slate-600" />
          <h3 className="font-semibold text-slate-800">Agenda</h3>
          {agendaGenerated && (
            <Badge variant="outline" className="text-emerald-600 border-emerald-200">
              <Wand2 className="w-3 h-3 mr-1" />
              AI Generated
            </Badge>
          )}
          {totalDuration > 0 && (
            <span className="text-sm text-slate-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {totalDuration} min total
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Button size="sm" onClick={handleSaveAgenda} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span className="ml-1">Save</span>
            </Button>
          )}
          
          {/* Templates Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="text-slate-600">
                <BookTemplate className="w-4 h-4 mr-1" />
                Templates
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              {/* Template Library */}
              <DropdownMenuItem onClick={() => setShowTemplateLibrary(true)}>
                <BookTemplate className="w-4 h-4 mr-2 text-primary-600" />
                <span className="font-medium">Browse Template Library</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              
              {agendaItems.length > 0 && (
                <>
                  <DropdownMenuItem onClick={() => setShowSaveTemplate(true)}>
                    <Save className="w-4 h-4 mr-2" />
                    Save as Template
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              
              {templates.length > 0 && (
                <>
                  <div className="px-2 py-1.5 text-xs font-medium text-slate-500">
                    My Templates
                  </div>
                  {templates.slice(0, 5).map((template) => (
                    <DropdownMenuItem
                      key={template.id}
                      onClick={() => handleApplyTemplate(template.id)}
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      <div className="flex-1 truncate">
                        <span>{template.name}</span>
                        {template.is_default && (
                          <Badge variant="outline" className="ml-2 text-xs py-0">
                            Default
                          </Badge>
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                  {templates.length > 5 && (
                    <div className="px-2 py-1 text-xs text-slate-400">
                      +{templates.length - 5} more in library
                    </div>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowWizard(true)}
            className="text-primary-600 border-primary-200 hover:bg-primary-50"
          >
            <Wand2 className="w-4 h-4 mr-1" />
            Generate Agenda
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowAddItem(true)}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Agenda Items List */}
      {agendaItems.length === 0 ? (
        <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
          <ListOrdered className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No agenda items yet</p>
          <p className="text-sm text-slate-400 mt-1">
            Use &quot;Generate Agenda&quot; for AI-powered creation or add items manually
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {agendaItems.map((item, index) => {
            const outcomeConfig = getOutcomeConfig(item.outcome_type);
            const OutcomeIcon = outcomeConfig.icon;
            
            return (
              <div
                key={item.id}
                className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors group"
              >
                {/* Order & Drag Handle */}
                <div className="flex flex-col items-center gap-1 pt-1">
                  <span className="text-xs font-medium text-slate-400 w-6 text-center">
                    {item.order}
                  </span>
                  <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleMoveItem(index, "up")}
                      disabled={index === 0}
                      className="p-0.5 hover:bg-slate-100 rounded disabled:opacity-30"
                    >
                      <ChevronUp className="w-3 h-3 text-slate-400" />
                    </button>
                    <button
                      onClick={() => handleMoveItem(index, "down")}
                      disabled={index === agendaItems.length - 1}
                      className="p-0.5 hover:bg-slate-100 rounded disabled:opacity-30"
                    >
                      <ChevronDown className="w-3 h-3 text-slate-400" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <Input
                      value={item.title}
                      onChange={(e) => handleUpdateItem(item.id, "title", e.target.value)}
                      className="font-medium text-slate-800 border-0 p-0 h-auto focus-visible:ring-0 bg-transparent"
                      placeholder="Item title..."
                    />
                  </div>
                  
                  {item.description && (
                    <p className="text-sm text-slate-500 mt-1">{item.description}</p>
                  )}
                  
                  <div className="flex items-center gap-3 mt-2">
                    {/* Outcome Type */}
                    <Select
                      value={item.outcome_type}
                      onValueChange={(value) => handleUpdateItem(item.id, "outcome_type", value)}
                    >
                      <SelectTrigger className="w-auto h-7 text-xs gap-1 border-0 p-0 pr-2">
                        <Badge className={`${outcomeConfig.color} text-xs`}>
                          <OutcomeIcon className="w-3 h-3 mr-1" />
                          {item.outcome_type}
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(OUTCOME_TYPES).map(([type, config]) => {
                          const Icon = config.icon;
                          return (
                            <SelectItem key={type} value={type}>
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4" />
                                {type}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>

                    {/* Duration */}
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <Clock className="w-3 h-3" />
                      <Input
                        type="number"
                        min="1"
                        max="180"
                        value={item.duration_mins || ""}
                        onChange={(e) => handleUpdateItem(item.id, "duration_mins", parseInt(e.target.value) || null)}
                        className="w-12 h-6 text-xs text-center p-1"
                        placeholder="--"
                      />
                      <span>min</span>
                    </div>

                    {/* Docs Required */}
                    {item.docs_required?.length > 0 && (
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <FileText className="w-3 h-3" />
                        <span>{item.docs_required.length} doc(s)</span>
                      </div>
                    )}

                    {item.is_recurring && (
                      <Badge variant="outline" className="text-xs text-slate-500">
                        Recurring
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <button
                  onClick={() => handleDeleteItem(item.id)}
                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Generate Agenda Wizard */}
      <Dialog open={showWizard} onOpenChange={setShowWizard}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-primary-600" />
              Generate Agenda
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Meeting Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Meeting Type</Label>
                <Select
                  value={wizardData.meeting_type}
                  onValueChange={(value) => setWizardData({ ...wizardData, meeting_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Board">Board Meeting</SelectItem>
                    <SelectItem value="Committee">Committee Meeting</SelectItem>
                    <SelectItem value="AGM">Annual General Meeting</SelectItem>
                    <SelectItem value="EGM">Extraordinary General Meeting</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Committee Name</Label>
                <Input
                  value={wizardData.committee_name}
                  onChange={(e) => setWizardData({ ...wizardData, committee_name: e.target.value })}
                  placeholder="e.g., Audit Committee"
                />
              </div>
            </div>

            {/* Objectives */}
            <div>
              <Label>Meeting Purpose / Objectives</Label>
              <Textarea
                value={wizardData.objectives}
                onChange={(e) => setWizardData({ ...wizardData, objectives: e.target.value })}
                placeholder="Describe the main purpose and objectives of this meeting..."
                rows={2}
              />
            </div>

            {/* Topics */}
            <div>
              <Label>Key Topics (one per line)</Label>
              <Textarea
                value={wizardData.topics}
                onChange={(e) => setWizardData({ ...wizardData, topics: e.target.value })}
                placeholder="Q3 Financial Results Review&#10;New Product Launch Strategy&#10;Budget Approval for 2025&#10;IT Security Update"
                rows={5}
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">
                Enter each topic on a new line. The AI will categorize and format them appropriately.
              </p>
            </div>

            {/* Recurring Items */}
            <div>
              <Label className="mb-3 block">Standard Agenda Items</Label>
              <div className="space-y-4">
                {/* Opening Items */}
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">Opening</p>
                  <div className="grid grid-cols-2 gap-2">
                    {RECURRING_ITEMS.filter(i => i.group === "opening").map((item) => (
                      <label key={item.key} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                        <Checkbox
                          checked={wizardData.recurring_toggles[item.key] || false}
                          onCheckedChange={() => toggleRecurring(item.key)}
                        />
                        <span className="text-sm">{item.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Updates */}
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">Standing Updates</p>
                  <div className="grid grid-cols-2 gap-2">
                    {RECURRING_ITEMS.filter(i => i.group === "updates").map((item) => (
                      <label key={item.key} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                        <Checkbox
                          checked={wizardData.recurring_toggles[item.key] || false}
                          onCheckedChange={() => toggleRecurring(item.key)}
                        />
                        <span className="text-sm">{item.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Business */}
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">Business</p>
                  <div className="grid grid-cols-2 gap-2">
                    {RECURRING_ITEMS.filter(i => i.group === "business").map((item) => (
                      <label key={item.key} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                        <Checkbox
                          checked={wizardData.recurring_toggles[item.key] || false}
                          onCheckedChange={() => toggleRecurring(item.key)}
                        />
                        <span className="text-sm">{item.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Closing */}
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">Closing</p>
                  <div className="grid grid-cols-2 gap-2">
                    {RECURRING_ITEMS.filter(i => i.group === "closing").map((item) => (
                      <label key={item.key} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                        <Checkbox
                          checked={wizardData.recurring_toggles[item.key] || false}
                          onCheckedChange={() => toggleRecurring(item.key)}
                        />
                        <span className="text-sm">{item.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Options */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={wizardData.include_timings}
                  onCheckedChange={(checked) => setWizardData({ ...wizardData, include_timings: checked })}
                />
                <span className="text-sm">Include suggested timings</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={wizardData.include_docs_checklist}
                  onCheckedChange={(checked) => setWizardData({ ...wizardData, include_docs_checklist: checked })}
                />
                <span className="text-sm">Include documents checklist</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWizard(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerateAgenda} disabled={generating}>
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Generate Agenda
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Preview Generated Agenda
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{previewItems.length} items generated</span>
                <span className="text-slate-600 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {previewItems.reduce((sum, item) => sum + (item.duration_mins || 0), 0)} min total
                </span>
              </div>
            </div>

            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {previewItems.map((item, index) => {
                const outcomeConfig = getOutcomeConfig(item.outcome_type);
                const OutcomeIcon = outcomeConfig.icon;
                
                return (
                  <div
                    key={item.id || index}
                    className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200"
                  >
                    <span className="text-sm font-medium text-slate-400 w-6 text-center pt-0.5">
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{item.title}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <Badge className={`${outcomeConfig.color} text-xs`}>
                          <OutcomeIcon className="w-3 h-3 mr-1" />
                          {item.outcome_type}
                        </Badge>
                        {item.duration_mins && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {item.duration_mins} min
                          </span>
                        )}
                        {item.is_recurring && (
                          <Badge variant="outline" className="text-xs">Recurring</Badge>
                        )}
                      </div>
                      {item.docs_required?.length > 0 && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-slate-500">
                          <FileText className="w-3 h-3" />
                          {item.docs_required.join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => { setShowPreview(false); setShowWizard(true); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Regenerate
            </Button>
            <Button onClick={handleApplyPreview} className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              Apply to Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Item Dialog */}
      <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Agenda Item</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Title</Label>
              <Input
                value={newItem.title}
                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                placeholder="Item title..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Outcome Type</Label>
                <Select
                  value={newItem.outcome_type}
                  onValueChange={(value) => setNewItem({ ...newItem, outcome_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(OUTCOME_TYPES).map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  min="1"
                  max="180"
                  value={newItem.duration_mins}
                  onChange={(e) => setNewItem({ ...newItem, duration_mins: parseInt(e.target.value) || 15 })}
                />
              </div>
            </div>
            
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Brief description..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItem(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddItem}>
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save as Template Dialog */}
      <Dialog open={showSaveTemplate} onOpenChange={setShowSaveTemplate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookTemplate className="w-5 h-5" />
              Save as Template
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Template Name *</Label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Monthly Board Meeting"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Optional description..."
                rows={2}
              />
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-sm text-slate-600">
                This will save the current agenda structure ({agendaItems.length} items) as a reusable template.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveTemplate(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveAsTemplate} 
              disabled={!templateName.trim() || savingTemplate}
            >
              {savingTemplate ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Library Dialog */}
      <Dialog open={showTemplateLibrary} onOpenChange={setShowTemplateLibrary}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookTemplate className="w-5 h-5 text-primary-600" />
              Governance Template Library
            </DialogTitle>
            <p className="text-sm text-slate-500">
              Pre-built templates for common governance meetings. Click to apply.
            </p>
          </DialogHeader>

          {/* Category Filter */}
          <div className="flex gap-2 py-2 border-b">
            <Button
              size="sm"
              variant={selectedCategory === "all" ? "default" : "outline"}
              onClick={() => setSelectedCategory("all")}
            >
              All Templates
            </Button>
            {Object.entries(templateCategories).map(([key, cat]) => (
              <Button
                key={key}
                size="sm"
                variant={selectedCategory === key ? "default" : "outline"}
                onClick={() => setSelectedCategory(key)}
              >
                {cat.name}
              </Button>
            ))}
          </div>

          {/* Template Grid */}
          <div className="flex-1 overflow-y-auto py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {libraryTemplates
                .filter(t => selectedCategory === "all" || t.category === selectedCategory)
                .map((template) => (
                  <div
                    key={template.id}
                    className={`border rounded-lg p-4 transition-colors ${
                      previewTemplate?.id === template.id 
                        ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200' 
                        : 'hover:border-primary-300 hover:bg-primary-50/50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-medium text-slate-800">{template.name}</h4>
                        {template.committee_name && (
                          <p className="text-xs text-slate-500">{template.committee_name}</p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {template.meeting_type}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                      {template.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <ListOrdered className="w-3 h-3" />
                          {template.items_count} items
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {template.total_duration_mins} min
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-xs h-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewTemplate(previewTemplate?.id === template.id ? null : template);
                          }}
                        >
                          {previewTemplate?.id === template.id ? 'Hide' : 'Preview'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCustomizeTemplate(template);
                          }}
                          data-testid={`customize-template-${template.id}`}
                        >
                          Customize
                        </Button>
                        <Button
                          size="sm"
                          className="text-xs h-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApplyLibraryTemplate(template.id);
                          }}
                          data-testid={`apply-template-${template.id}`}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                    
                    {/* Preview Items */}
                    {previewTemplate?.id === template.id && (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <p className="text-xs font-medium text-slate-500 mb-2">Agenda Items:</p>
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {template.items.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm py-1">
                              <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-xs font-medium">
                                {idx + 1}
                              </span>
                              <span className="flex-1 text-slate-700">{item.title}</span>
                              <Badge variant="outline" className="text-xs py-0">
                                {item.outcome_type}
                              </Badge>
                              {item.duration_mins && (
                                <span className="text-xs text-slate-400">{item.duration_mins}m</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowTemplateLibrary(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Customization Modal */}
      <Dialog open={showCustomizeModal} onOpenChange={setShowCustomizeModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-primary-600" />
              Customize Template: {customizeTemplate?.name}
            </DialogTitle>
            <p className="text-sm text-slate-500">
              Edit, add, or remove agenda items before applying to your meeting.
            </p>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4 space-y-3">
            {customizedItems.map((item, index) => (
              <div 
                key={item.id} 
                className="border rounded-lg p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                data-testid={`customized-item-${index}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary-100 text-primary-700 text-sm font-medium flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1 space-y-3">
                    <div>
                      <Label className="text-xs text-slate-500">Title</Label>
                      <Input
                        value={item.title}
                        onChange={(e) => handleUpdateCustomizedItem(index, 'title', e.target.value)}
                        placeholder="Agenda item title"
                        className="mt-1"
                        data-testid={`item-title-${index}`}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Duration (mins)</Label>
                        <Input
                          type="number"
                          value={item.duration_mins || ""}
                          onChange={(e) => handleUpdateCustomizedItem(index, 'duration_mins', parseInt(e.target.value) || 0)}
                          placeholder="10"
                          className="mt-1"
                          data-testid={`item-duration-${index}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Outcome Type</Label>
                        <Select
                          value={item.outcome_type || "discussion"}
                          onValueChange={(value) => handleUpdateCustomizedItem(index, 'outcome_type', value)}
                        >
                          <SelectTrigger className="mt-1" data-testid={`item-outcome-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="discussion">Discussion</SelectItem>
                            <SelectItem value="decision">Decision Required</SelectItem>
                            <SelectItem value="information">Information Only</SelectItem>
                            <SelectItem value="resolution">Resolution</SelectItem>
                            <SelectItem value="approval">Approval Required</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Description (optional)</Label>
                      <Textarea
                        value={item.description || ""}
                        onChange={(e) => handleUpdateCustomizedItem(index, 'description', e.target.value)}
                        placeholder="Additional details..."
                        className="mt-1 min-h-[60px]"
                        data-testid={`item-description-${index}`}
                      />
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => handleRemoveCustomizedItem(index)}
                    data-testid={`remove-item-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}

            {customizedItems.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <ListOrdered className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No items yet. Add your first agenda item.</p>
              </div>
            )}
          </div>

          <div className="border-t pt-4 flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handleAddCustomizedItem}
              className="flex items-center gap-2"
              data-testid="add-custom-item-btn"
            >
              <Plus className="w-4 h-4" />
              Add Item
            </Button>

            <div className="flex items-center gap-3">
              <div className="text-sm text-slate-500">
                {customizedItems.length} items • 
                {customizedItems.reduce((sum, item) => sum + (item.duration_mins || 0), 0)} min total
              </div>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCustomizeModal(false);
                  setShowTemplateLibrary(true);
                }}
              >
                Back
              </Button>
              <Button
                onClick={handleApplyCustomizedTemplate}
                disabled={customizedItems.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="apply-customized-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Apply to Meeting
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
